import { Injectable, signal } from '@angular/core';
import {
  IWHFlowMetadataTranslationModel,
  WHMetadataDataService,
} from '@workheld/workheld-shared-lib';
import { sortEnum } from '../app-shared/helpers/enumSort.helper';

export interface MetadataEnum {
  id: string;
  translateKey: string;
}

/**
 * Creates a Proxy that returns a fallback value for any missing property.
 * This prevents TypeError crashes when an enum category doesn't exist in the backend metadata.
 *
 * Example: `$ENUMS_OBJECT().MISSING_ENUM[key]` returns `undefined` instead of throwing.
 * Example: `$ENUMS().MISSING_ENUM.map(...)` returns `[]` instead of throwing.
 */
function createSafeEnumProxy<T extends object>(
  target: T,
  fallback: () => any,
): T {
  return new Proxy(target, {
    get(obj, prop) {
      if (prop in obj || typeof prop === 'symbol') {
        return obj[prop];
      }
      return fallback();
    },
  });
}

const SAFE_ENUMS_EMPTY: IWHFlowMetadataTranslationModel = createSafeEnumProxy(
  {} as IWHFlowMetadataTranslationModel,
  () => [],
);
const SAFE_ENUMS_OBJECT_EMPTY: Record<
  string,
  Record<string, string>
> = createSafeEnumProxy(
  {} as Record<string, Record<string, string>>,
  () => ({}),
);

@Injectable({
  providedIn: 'root',
})
export class WHEnumDataService {
  $ENUMS = signal<IWHFlowMetadataTranslationModel>(SAFE_ENUMS_EMPTY);
  $ENUMS_OBJECT = signal<Record<string, Record<string, string>>>(
    SAFE_ENUMS_OBJECT_EMPTY,
  );

  constructor(private metadataDataService: WHMetadataDataService) {
    this.metadataDataService.metadataTranslation$.subscribe((metadata) => {
      if (metadata) {
        const preparedMetadata = this._prepareMetadata(metadata);

        this.$ENUMS.set(preparedMetadata);
        this.$ENUMS_OBJECT.set(
          createSafeEnumProxy(metadata as any, () => ({})),
        );
      }
    });
  }

  private _prepareMetadata(metadata: any): IWHFlowMetadataTranslationModel {
    const preparedMetadata: any = {};
    Object.keys(metadata).forEach((key) => {
      preparedMetadata[key] = sortEnum(
        this._convertToKeyValueArray(metadata[key]),
      );
    });
    return createSafeEnumProxy(preparedMetadata, () => []);
  }

  private _convertToKeyValueArray(obj: {
    [key: string]: string;
  }): MetadataEnum[] {
    return Object.entries(obj).map(([key, value]) => ({
      id: key,
      translateKey: value,
    }));
  }
}
